import { collection, doc, addDoc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from './config';
import { withTimeout, queryCacheFirst, docCacheFirst, stripUndefined } from './utils';
import { PFMotorInput, PFSettings, DEFAULT_PF_SETTINGS } from '../modules/powerFactor/powerFactorCalc';

/* One document per motor (same reasoning as Facility assets: two people editing two motors can
   never overwrite each other), plus one settings document per project. */

export interface PFMotor extends PFMotorInput {
  id: string;
  createdAt: string;
  updatedAt: string;
}

const motorsCol = (projectId: string) => collection(db, 'projects', projectId, 'powerFactorMotors');
const settingsRef = (projectId: string) => doc(db, 'projects', projectId, 'powerFactorSettings', 'settings');

const iso = (v: any) => v?.toDate?.()?.toISOString() ?? v ?? '';

export const getPFMotors = (projectId: string, onRefresh?: (m: PFMotor[]) => void): Promise<PFMotor[]> =>
  queryCacheFirst(motorsCol(projectId), snap =>
    snap.docs
      .map(d => {
        const data = d.data();
        return { ...data, id: d.id, createdAt: iso(data.createdAt), updatedAt: iso(data.updatedAt) } as PFMotor;
      })
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt)), onRefresh);

/** Creates when `id` is omitted; otherwise replaces that motor. Returns the id. */
export const savePFMotor = async (projectId: string, motor: PFMotorInput, id?: string): Promise<string> => {
  const now = Timestamp.now();
  const clean = stripUndefined({ ...motor, projectId, updatedAt: now });
  if (id) {
    await withTimeout(setDoc(doc(motorsCol(projectId), id), clean, { merge: true }));
    return id;
  }
  const ref = await withTimeout(addDoc(motorsCol(projectId), { ...clean, createdAt: now }));
  return ref.id;
};

export const deletePFMotor = async (projectId: string, id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(motorsCol(projectId), id)));
};

export const getPFSettings = (projectId: string, onRefresh?: (s: PFSettings) => void): Promise<PFSettings> =>
  docCacheFirst(settingsRef(projectId),
    s => ({ ...DEFAULT_PF_SETTINGS, ...(s.exists() ? (s.data() as Partial<PFSettings>) : {}) }), onRefresh);

export const savePFSettings = async (projectId: string, settings: PFSettings): Promise<void> => {
  await withTimeout(setDoc(settingsRef(projectId), { ...settings, updatedAt: Timestamp.now() }));
};
